import { countries } from '@/common/constant';
import type { PairingFilter, Supplier } from '@/common/db-typebox';
import { RangeInput } from '@/components/RangeInput';
import { useCurrencySymbol } from '@/hooks/useCurrencySymbol';
import { useTreatyClient } from '@/lib/client';
import { CloseOutlined } from '@ant-design/icons';
import {
  Button,
  Card,
  Checkbox,
  Form,
  Input,
  InputNumber,
  Select,
  Skeleton,
  Slider,
  Space,
  Tree,
  message,
} from 'antd';
import { useEffect, useState } from 'react';
import type { PairingOrMenu } from '../types';
import { WineTypeSelect } from './WineTypeSelect';

interface PairingFormProps {
  item?: PairingOrMenu | null;
  onSuccess?: (id: string) => void;
  onClose?: () => void;
}

interface TreeDataNode {
  title: string;
  key: string;
  children?: TreeDataNode[];
}

export function PairingForm({ item, onSuccess, onClose }: PairingFormProps) {
  const [form] = Form.useForm();
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [suppliersLoading, setSuppliersLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [checkedKeys, setCheckedKeys] = useState<string[]>([]);
  const { priceRange, symbol } = useCurrencySymbol();

  const client = useTreatyClient();

  const fetchSuppliers = async () => {
    try {
      setSuppliersLoading(true);
      const response = await client.api.supplier.list.get();
      if (response.data?.success) {
        setSuppliers(response.data.data.suppliers);
        return;
      }
      throw new Error(
        response.error?.value.message || 'Failed to fetch suppliers',
      );
    } catch (error) {
      console.error(error);
      message.error('Failed to fetch suppliers');
    } finally {
      setSuppliersLoading(false);
    }
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    fetchSuppliers();
  }, []);

  const onTreeCheck = (checked: string[]) => {
    setCheckedKeys(checked);
    form.setFieldValue(['filter', 'suppliers'], checked);
  };

  useEffect(() => {
    if (item) {
      if (item.type === 'pairing') {
        form.setFieldsValue({
          dishDescription: item.pairing.params.dish_description,
          filter: item.pairing.params.filter,
        });
        setCheckedKeys(item.pairing.params.filter.suppliers || []);
      } else if (item.type === 'menu') {
        if (item.menu.pairing) {
          form.setFieldsValue({
            dishDescription: item.menu.pairing.params.dish_description,
            filter: item.menu.pairing.params.filter,
          });
          setCheckedKeys(item.menu.pairing.params.filter.suppliers || []);
        } else {
          form.setFieldsValue({
            filter: {
              include_user_wine: true,
              limit: 10,
            },
            dishDescription: `${item.menu.name}${item.menu.description ? ` - ${item.menu.description}` : ''}${item.menu.tags && item.menu.tags.length > 0 ? ` [Tags: ${item.menu.tags.join(', ')}]` : ''}`,
          });
          setCheckedKeys([]);
        }
      }
    }

    return () => {
      form.resetFields();
      setCheckedKeys([]);
    };
  }, [item, form]);

  const treeData: TreeDataNode[] = [
    {
      title: 'All Suppliers',
      key: 'all',
      children: suppliers.map((supplier) => ({
        title: supplier.name,
        key: supplier.id,
      })),
    },
  ];

  const onFinish = async (values: {
    dishDescription: string;
    filter: PairingFilter;
  }) => {
    try {
      setSubmitLoading(true);
      if (!values.dishDescription?.trim()) {
        message.error('Dish description is required');
        return;
      }

      const response = await client.api.pairing.create.post({
        menu_id: item?.type === 'menu' ? item.menu.id : undefined,
        operation_modes: ['dish_analysis', 'wine_recommendation'],
        dish_description: values.dishDescription.trim(),
        filter: {
          ...values.filter,
          suppliers: values.filter.suppliers?.filter(
            (supplier) => supplier !== 'all',
          ),
        },
      });
      if (response.data?.success) {
        message.success('Pairing created successfully');
        onSuccess?.(response.data.id);
      } else {
        message.error(
          response.error?.value.message || 'Failed to create pairing',
        );
      }
    } catch (error) {
      console.error(error);
      message.error('Failed to create pairing');
    } finally {
      setSubmitLoading(false);
    }
  };

  return (
    <div className="flex-1 overflow-auto">
      <div className="flex justify-end">
        <Button type="text" onClick={() => onClose?.()}>
          <CloseOutlined />
        </Button>
      </div>
      <Form layout="vertical" form={form} onFinish={onFinish}>
        <Form.Item
          label="Dish Description"
          name="dishDescription"
          rules={[{ required: true, message: 'Dish description is required' }]}
        >
          <Input.TextArea
            placeholder="Describe your dish in detail (e.g., 'Herb-crusted rack of lamb with red wine reduction')"
            rows={4}
            allowClear
            showCount
            maxLength={1000}
          />
        </Form.Item>
        <Form.Item label="Wine Preferences">
          <Card
            size="small"
            styles={{
              body: {
                padding: 0,
              },
            }}
          >
            <div className="flex">
              <div className="flex-1 border-r p-4">
                <Space direction="vertical" className="w-full">
                  <Form.Item
                    name={['filter', 'include_user_wine']}
                    valuePropName="checked"
                    noStyle
                  >
                    <Checkbox>My Wine List</Checkbox>
                  </Form.Item>
                  {suppliersLoading ? (
                    <Skeleton active paragraph={{ rows: 1 }} />
                  ) : (
                    <Form.Item name={['filter', 'suppliers']} noStyle>
                      <Tree
                        checkable
                        treeData={treeData}
                        defaultExpandAll
                        checkedKeys={checkedKeys}
                        onCheck={(checked) => onTreeCheck(checked as string[])}
                      />
                    </Form.Item>
                  )}
                </Space>
              </div>
              <div className="w-96 p-4">
                <Space direction="vertical" className="w-full">
                  <Form.Item label="Wine Type" name={['filter', 'wine_types']}>
                    <WineTypeSelect />
                  </Form.Item>
                  <Form.Item
                    label="Wine result amount limit"
                    name={['filter', 'limit']}
                  >
                    <InputNumber
                      className="w-24"
                      placeholder="limit wine"
                      precision={0}
                      min={0}
                      step={1}
                    />
                  </Form.Item>
                  <Form.Item label="Country" name={['filter', 'countries']}>
                    <Select
                      mode="multiple"
                      allowClear
                      maxTagCount={5}
                      placeholder="Select countries"
                      options={countries.map((country) => ({
                        label: country,
                        value: country,
                      }))}
                    />
                  </Form.Item>
                  <Form.Item label="Price Range">
                    <Form.Item name={['filter', 'price_range']} noStyle>
                      <RangeInput min={priceRange.min} prefix={symbol} />
                    </Form.Item>
                  </Form.Item>
                  <Form.Item label="Vintage Year">
                    <Form.Item name={['filter', 'year']} noStyle>
                      <RangeInput
                        min={1990}
                        max={new Date().getFullYear()}
                        precision={0}
                      />
                    </Form.Item>
                  </Form.Item>
                  <Form.Item
                    label="Minimum Score"
                    name={['filter', 'min_score']}
                  >
                    <Slider
                      min={0}
                      max={100}
                      marks={{
                        0: '0',
                        25: '25',
                        50: '50',
                        75: '75',
                        100: '100',
                      }}
                    />
                  </Form.Item>
                  <Form.Item label="Alcohol %">
                    <Space.Compact className="w-full">
                      <Form.Item
                        name={['filter', 'alcohol_range', 'min']}
                        noStyle
                      >
                        <InputNumber
                          className="w-24"
                          placeholder="Min"
                          min={0}
                          max={100}
                          step={0.1}
                        />
                      </Form.Item>
                      <Input
                        className="site-input-split"
                        style={{
                          width: 30,
                          borderLeft: 0,
                          borderRight: 0,
                          pointerEvents: 'none',
                        }}
                        placeholder="~"
                        disabled
                      />
                      <Form.Item
                        name={['filter', 'alcohol_range', 'max']}
                        noStyle
                      >
                        <InputNumber
                          className="w-24"
                          placeholder="Max"
                          min={0}
                          max={100}
                          step={0.1}
                        />
                      </Form.Item>
                    </Space.Compact>
                  </Form.Item>
                  <Form.Item
                    name={['filter', 'allow_null_reviews']}
                    valuePropName="checked"
                  >
                    <Checkbox>Include Unreviewed Wines</Checkbox>
                  </Form.Item>
                  <Form.Item
                    name={['filter', 'include_no_price']}
                    valuePropName="checked"
                  >
                    <Checkbox>Include Wines Without Price</Checkbox>
                  </Form.Item>
                </Space>
              </div>
            </div>
          </Card>
        </Form.Item>
        <Form.Item>
          <Button
            type="primary"
            block
            htmlType="submit"
            loading={submitLoading}
          >
            Find Pairings
          </Button>
        </Form.Item>
      </Form>
    </div>
  );
}
